import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ClientResponseError } from 'pocketbase';
import { appRoutes, publicRoutes } from '@config/routes';
import { useAuth } from '@shared/hooks/useAuth';
import { useAuthStore, type User } from '@state/auth.store';

const loginSchema = z.object({
  identity: z.string().min(1, 'Vui lòng nhập email hoặc tên đăng nhập'),
  password: z.string().min(1, 'Vui lòng nhập mật khẩu'),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { loginUser, isAuthenticated, user } = useAuth();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identity: '',
      password: '',
      rememberMe: false,
    },
  });

  useEffect(() => {
    if (isAuthenticated && user && !user.isSuperAdmin) {
      navigate(appRoutes.dashboard, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const onSubmit = async (values: LoginFormValues) => {
    setIsSubmitting(true);

    try {
      const authData = await loginUser(values.identity, values.password);

      const userRecord = authData.record as User;
      const updatedUser: User = {
        ...userRecord,
        rememberMe: values.rememberMe,
      };

      useAuthStore.getState().setUser(updatedUser);

      localStorage.setItem('hrm-remember-me', JSON.stringify(values.rememberMe));

      toast.success('Đăng nhập thành công');
      navigate(appRoutes.dashboard, { replace: true });
    } catch (error) {
      let message = 'Đăng nhập thất bại. Vui lòng thử lại.';

      if (error instanceof ClientResponseError) {
        if (error.status === 400) {
          message = 'Thông tin đăng nhập không chính xác.';
        } else if (error.status >= 500) {
          message = 'Máy chủ gặp sự cố, vui lòng thử lại sau.';
        }
      }

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-sans text-slate-600 min-h-screen w-full relative overflow-y-auto flex items-center justify-center py-6 selection:bg-primary-soft selection:text-primary">
      {/* Animated background blobs */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-100/60 rounded-full blur-[100px] animate-blob mix-blend-multiply opacity-70"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-100/60 rounded-full blur-[100px] animate-blob mix-blend-multiply opacity-70" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-[30%] right-[20%] w-[40%] h-[40%] bg-blue-100/60 rounded-full blur-[100px] animate-blob mix-blend-multiply opacity-70" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="w-full px-6 flex justify-center z-10 my-auto">
        <div className="glass-card w-full max-w-[480px] p-6 md:p-8 lg:p-12 rounded-4xl shadow-soft flex flex-col items-center transform transition-all hover:scale-[1.005] duration-500">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6 md:mb-8 text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white shadow-glow mb-4 transform rotate-3 hover:rotate-6 transition-transform duration-300">
              <span className="material-symbols-outlined text-[28px]">spa</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">Luna HRM</h1>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em]">Employee Happiness Platform</p>
          </div>

          {/* Heading */}
          <div className="w-full mb-6 md:mb-8 text-center md:text-left">
            <h2 className="text-3xl font-extrabold text-slate-800 mb-2">Welcome back 👋</h2>
            <p className="text-slate-500 font-medium">We're glad to see you again.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-5">
            {/* Email/Identity Input */}
            <div className="group">
              <label className="block text-sm font-bold text-slate-700 mb-2 ml-1" htmlFor="identity">
                Work Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-slate-400 text-[20px] group-focus-within:text-primary transition-colors">
                    mail
                  </span>
                </div>
                <input
                  type="text"
                  id="identity"
                  placeholder="sophia@lunahrm.com"
                  className={`block w-full pl-11 pr-4 py-4 bg-white/50 border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/50 focus:bg-white transition-all shadow-input font-medium ${
                    errors.identity ? 'border-red-300 focus:border-red-500 focus:ring-red-500/10' : ''
                  }`}
                  autoFocus
                  {...register('identity')}
                />
              </div>
              {errors.identity && (
                <div className="mt-2 ml-1 text-sm text-red-600">{errors.identity.message}</div>
              )}
            </div>

            {/* Password Input */}
            <div className="group">
              <label className="block text-sm font-bold text-slate-700 mb-2 ml-1" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-slate-400 text-[20px] group-focus-within:text-primary transition-colors">
                    lock
                  </span>
                </div>
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  id="password"
                  placeholder="••••••••"
                  className={`block w-full pl-11 pr-12 py-4 bg-white/50 border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/50 focus:bg-white transition-all shadow-input font-medium ${
                    errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500/10' : ''
                  }`}
                  {...register('password')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-primary transition-colors"
                  onClick={() => setIsPasswordVisible((prev) => !prev)}
                  aria-label={isPasswordVisible ? 'Ẩn mật khẩu' : 'Hiển thị mật khẩu'}
                >
                  <span className="material-symbols-outlined text-[20px]">
                    {isPasswordVisible ? 'visibility' : 'visibility_off'}
                  </span>
                </button>
              </div>
              {errors.password && (
                <div className="mt-2 ml-1 text-sm text-red-600">{errors.password.message}</div>
              )}
            </div>

            {/* Remember Me and Forgot Password */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center cursor-pointer group">
                <input
                  className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary/20 cursor-pointer"
                  type="checkbox"
                  {...register('rememberMe')}
                />
                <span className="ml-2 text-sm font-semibold text-slate-500 group-hover:text-slate-700 transition-colors">
                  Remember me
                </span>
              </label>
              <Link
                to={publicRoutes.forgotPassword}
                className="text-sm font-bold text-primary hover:text-secondary transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              className="w-full py-3 px-5 bg-gradient-to-r from-primary to-secondary hover:from-primary-light hover:to-[#be7af7] text-white rounded-2xl font-bold text-sm shadow-lg shadow-primary/30 hover:shadow-glow transform hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              type="submit"
              disabled={isSubmitting}
            >
              <span>{isSubmitting ? 'Đang đăng nhập...' : 'Sign In'}</span>
              {!isSubmitting && <span className="material-symbols-outlined text-[18px]">arrow_forward</span>}
            </button>
          </form>

          {/* Divider */}
          <div className="relative w-full my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 text-xs font-bold text-slate-400 uppercase tracking-widest bg-white/40 backdrop-blur-sm rounded-full">
                or continue with
              </span>
            </div>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-2 gap-4 w-full">
            <button
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-2xl hover:bg-slate-50 hover:border-slate-300 transition-all group"
              type="button"
              aria-label="Đăng nhập với Google"
            >
              <svg
                className="w-5 h-5 group-hover:scale-110 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z"
                  fill="#4285F4"
                ></path>
                <path
                  d="M12.24 24.0008C15.4766 24.0008 18.2058 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.24 24.0008Z"
                  fill="#34A853"
                ></path>
                <path
                  d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.5166C-0.18551 10.0056 -0.18551 14.0004 1.5166 17.3912L5.50253 14.3003Z"
                  fill="#FBBC05"
                ></path>
                <path
                  d="M12.24 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.034466 12.24 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50253 9.70575C6.45046 6.86143 9.10935 4.74966 12.24 4.74966Z"
                  fill="#EA4335"
                ></path>
              </svg>
              <span className="text-sm font-bold text-slate-600 group-hover:text-slate-800">Google</span>
            </button>
            <button
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-2xl hover:bg-slate-50 hover:border-slate-300 transition-all group"
              type="button"
              aria-label="Đăng nhập với Microsoft"
            >
              <svg
                className="w-5 h-5 group-hover:scale-110 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.55 11.55H0.550049V0.550049H11.55V11.55ZM23.45 11.55H12.45V0.550049H23.45V11.55ZM11.55 23.45H0.550049V12.45H11.55V23.45ZM23.45 23.45H12.45V12.45H23.45V23.45Z"
                  fill="#F25022"
                ></path>
                <path d="M11.55 11.55H0.550049V0.550049H11.55V11.55Z" fill="#F25022"></path>
                <path d="M23.45 11.55H12.45V0.550049H23.45V11.55Z" fill="#7FBA00"></path>
                <path d="M11.55 23.45H0.550049V12.45H11.55V23.45Z" fill="#00A4EF"></path>
                <path d="M23.45 23.45H12.45V12.45H23.45V23.45Z" fill="#FFB900"></path>
              </svg>
              <span className="text-sm font-bold text-slate-600 group-hover:text-slate-800">Microsoft</span>
            </button>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-4">
            <p className="text-center text-sm font-medium text-slate-400">
              Don't have an account?{' '}
              <Link to={publicRoutes.register} className="text-primary hover:text-secondary font-bold transition-colors ml-1">
                Contact your HR team
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
